// A refused save (a 409, or a live conflict refused locally) opens the
// conflict dialog. A 409 has no data: the stored rows of its conflicts are
// fetched first (the worker's reloadItems, through draft.incoming), so a
// conflict no socket event told becomes a live conflict of its draft, or the
// conflict held by the open form (the hold), with the server row
// (draft.conflictVersions: "What changed", Rebase). A conflict the store
// knows already (the same server version) is not fetched again. A fetch that
// fails still opens the dialog (with no rows to compare).

import dataWorker from "../useData/worker";
import { db } from "../../db";
import draft from "./instance";
import { draftKey } from "./store";
import { showConflicts } from "./conflictDialog";

const rowsOf = (conflicts = []) =>
  conflicts.map(({ type, accessor, id }) => ({
    accessor: type ?? accessor,
    id
  }));

const versionOrNull = (version) =>
  version === null || version === undefined ? null : Number(version);

// The store has the server row of the conflict, at the version the conflict
// says (a 409's storedVersion, a live conflict's): nothing to fetch
const known = (store, conflict) => {
  const versions = store.conflictVersions?.(
    conflict.type ?? conflict.accessor,
    conflict.id
  );
  if (!versions) return false;
  const theirs = versions.theirs
    ? versionOrNull(versions.theirs.version ?? 0)
    : null;
  return theirs === versionOrNull(conflict.storedVersion);
};

const reloadWithWorker = (rows) => dataWorker.reloadItems(rows);

// The mineOf(accessor, id) of a draft: the user's row of a drafted row in
// conflict, for "What changed" and Rebase (the journal's row; null when
// removed in the draft), undefined when no draft holds the row (e.g. the row
// of an open form: the Item gives its form values for it).
export const draftMineOf = (accessor, id, store = draft) =>
  store.ownerOf(accessor, id) ? store.currentOf(accessor, id) : undefined;

const peekStored = (accessor, id) => db.peek(accessor, id);

// A conflict of a refused save whose row, stored now (fetched), is a save
// of ours at the version the server refused with, with no conflict held or
// live for it (draft: OUR OWN SAVES): that save had gone through (its
// response lost; Save clicked again after "Not saved"). Nothing to show:
// the row is the base of its draft entry, or of the open form, already.
export function ownSavedConflict(
  conflict,
  { store = draft, peek = peekStored } = {}
) {
  const accessor = conflict?.type ?? conflict?.accessor;
  if (!accessor || conflict.reason === "exists") return false;
  if (conflict.storedVersion === null || conflict.storedVersion === undefined)
    return false;
  const row = peek(accessor, conflict.id);
  return (
    row !== undefined &&
    Number(row.version ?? 0) === Number(conflict.storedVersion) &&
    Boolean(store.isOwnSave?.(accessor, conflict.id, row)) &&
    (store.conflictsOf?.([draftKey(accessor, conflict.id)]) ?? []).length === 0
  );
}

// payload: what showConflicts takes ({ conflicts, onDiscard, onRebase,
// mineOf }: onRebase(items), mineOf(accessor, id), see conflictDialog.js),
// and onOwnSaved(conflicts): told of the conflicts that are a save of ours
// once fetched (ownSavedConflict), which the dialog leaves out; no dialog
// when none is left. Resolves { shown, own } once the dialog is open (or
// not: shown false).
// deps (tests): reloadItems(rows) (default: the worker's), store (default:
// the app's draft), show (default: showConflicts), peek(accessor, id) (the
// stored row; default: MemoryDB's).
export async function presentConflicts(
  payload = {},
  {
    reloadItems = reloadWithWorker,
    store = draft,
    show = showConflicts,
    peek = peekStored
  } = {}
) {
  const { onOwnSaved, ...dialog } = payload;
  const conflicts = dialog.conflicts ?? [];
  const missing = rowsOf(
    conflicts.filter((conflict) => !known(store, conflict))
  );

  if (missing.length) {
    try {
      await reloadItems(missing);
    } catch (error) {
      console.warn("⚠️ CONFLICTS: the stored rows could not be loaded", error);
    }
  }

  const own = conflicts.filter((conflict) =>
    ownSavedConflict(conflict, { store, peek })
  );
  const others = conflicts.filter((conflict) => !own.includes(conflict));
  if (own.length) onOwnSaved?.(own);
  // Nothing at all to show: the dialog says so, as before
  if (others.length || !conflicts.length)
    show({ ...dialog, conflicts: others });
  return { shown: others.length > 0 || !conflicts.length, own };
}
